import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/* ===================================================== */
/* 🔔 NOTIFICATION ITEM                                  */
/* ===================================================== */

/**
 * NotificationItem — HeadwaySpot
 *
 * Displays:
 * - Notification title
 * - Message
 * - Notification type
 * - Timestamp
 * - Read/unread state
 * - Mark Read action
 * - View action
 * - Optional redirectTo route
 *
 * Props:
 * - notification: notification object
 * - isRead: optional explicit read state
 * - onMarkRead: async/sync callback receiving notification ID
 * - onView: async/sync callback receiving notification object
 * - className: optional additional classes
 */
export default function NotificationItem({
  notification = {},
  isRead,
  onMarkRead,
  onView,
  className = "",
}) {
  const [actionLoading, setActionLoading] = useState(false);

  /* ===================================================== */
  /* 🔑 NORMALIZED DATA                                    */
  /* ===================================================== */

  const id = useMemo(
    () =>
      String(
        notification?._id ||
          notification?.id ||
          ""
      ),
    [notification]
  );

  const title =
    typeof notification?.title === "string" &&
    notification.title.trim()
      ? notification.title.trim()
      : "Notification";

  const message =
    typeof notification?.message === "string"
      ? notification.message.trim()
      : "";

  const type = useMemo(() => {
    const value = String(
      notification?.type || "system"
    )
      .trim()
      .toLowerCase();

    /*
     * Backend-supported notification types:
     * task | milestone | investor | system
     */
    return [
      "task",
      "milestone",
      "investor",
      "system",
    ].includes(value)
      ? value
      : "system";
  }, [notification]);

  const redirectTo =
    typeof notification?.redirectTo === "string" &&
    notification.redirectTo.trim()
      ? notification.redirectTo.trim()
      : "";

  const read =
    typeof isRead === "boolean"
      ? isRead
      : typeof notification?.isRead === "boolean"
        ? notification.isRead
        : typeof notification?.read === "boolean"
          ? notification.read
          : typeof notification?.seen === "boolean"
            ? notification.seen
            : false;

  /* ===================================================== */
  /* 🕒 DATE                                               */
  /* ===================================================== */

  const formattedDate = useMemo(() => {
    if (!notification?.createdAt) {
      return "";
    }

    const date = new Date(
      notification.createdAt
    );

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  }, [notification]);

  /* ===================================================== */
  /* 🏷 TYPE BADGE                                         */
  /* ===================================================== */

  const typeBadge = useMemo(() => {
    const base =
      "text-[11px] font-medium rounded-md border px-2 py-0.5 capitalize tracking-wide";

    switch (type) {
      case "task":
        return `${base} border-blue-200 bg-blue-50 text-blue-700`;

      case "milestone":
        return `${base} border-violet-200 bg-violet-50 text-violet-700`;

      case "investor":
        return `${base} border-emerald-200 bg-emerald-50 text-emerald-700`;

      case "system":
      default:
        return `${base} border-slate-200 bg-slate-50 text-slate-700`;
    }
  }, [type]);

  /* ===================================================== */
  /* 🔐 MARK READ                                          */
  /* ===================================================== */

  const handleMarkRead = useCallback(
    async (event) => {
      /*
       * Prevent a button click from also triggering a
       * parent/action handler unexpectedly.
       */
      event?.preventDefault?.();
      event?.stopPropagation?.();

      if (
        read ||
        !id ||
        typeof onMarkRead !== "function" ||
        actionLoading
      ) {
        return;
      }

      setActionLoading(true);

      try {
        await onMarkRead(id);
      } catch (error) {
        /*
         * The parent owns user-facing error handling.
         * We deliberately do not swallow the rejected
         * promise silently.
         */
        console.error(
          "[NotificationItem] Failed to mark notification as read:",
          error
        );
      } finally {
        setActionLoading(false);
      }
    },
    [
      actionLoading,
      id,
      onMarkRead,
      read,
    ]
  );

  /* ===================================================== */
  /* 👁 VIEW                                              */
  /* ===================================================== */

  const handleView = useCallback(
    async (event) => {
      event?.stopPropagation?.();

      if (
        !id ||
        actionLoading
      ) {
        return;
      }

      /*
       * If the notification is already read, there is no
       * reason to call the read endpoint again.
       */
      if (
        read ||
        typeof onView !== "function"
      ) {
        return;
      }

      setActionLoading(true);

      try {
        await onView(notification);
      } catch (error) {
        console.error(
          "[NotificationItem] Failed to process notification view:",
          error
        );
      } finally {
        setActionLoading(false);
      }
    },
    [
      actionLoading,
      id,
      notification,
      onView,
      read,
    ]
  );

  /* ===================================================== */
  /* 🔗 LINK VIEW                                         */
  /* ===================================================== */

  const handleLinkView = useCallback(
    async (event) => {
      /*
       * We must not preventDefault here because Link needs
       * React Router to perform navigation.
       */
      if (
        !id ||
        read ||
        typeof onMarkRead !== "function" ||
        actionLoading
      ) {
        return;
      }

      /*
       * Mark read before navigation.
       * The parent handles the API request and state update.
       */
      setActionLoading(true);

      try {
        await onMarkRead(id);
      } catch (error) {
        /*
         * Do not block navigation if marking read fails.
         * The user should still be able to access the
         * destination.
         */
        console.error(
          "[NotificationItem] Failed to mark notification as read before navigation:",
          error
        );
      } finally {
        setActionLoading(false);
      }
    },
    [
      actionLoading,
      id,
      onMarkRead,
      read,
    ]
  );

  /* ===================================================== */
  /* 🖥 RENDER                                             */
  /* ===================================================== */

  return (
    <li
      className={[
        "group relative border-b bg-white p-4 last:border-none",
        "transition-colors hover:bg-gray-50",
        !read ? "pl-5" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* ================================================= */}
      {/* UNREAD INDICATOR                                  */}
      {/* ================================================= */}

      {!read && (
        <span
          aria-hidden="true"
          className="absolute bottom-0 left-0 top-0 w-1 rounded-r bg-indigo-500"
        />
      )}

      <div className="flex items-start justify-between gap-4">
        {/* ================================================= */}
        {/* CONTENT                                           */}
        {/* ================================================= */}

        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex flex-wrap items-center gap-2">
            <span
              className={[
                "truncate text-sm font-medium",
                read
                  ? "text-gray-600"
                  : "text-gray-900",
              ].join(" ")}
            >
              {title}
            </span>

            <span className={typeBadge}>
              {type}
            </span>

            {!read && (
              <span className="rounded-md border border-indigo-200 bg-indigo-100 px-2 py-[2px] text-[10px] font-medium text-indigo-700">
                New
              </span>
            )}
          </div>

          {message && (
            <p
              className={[
                "mt-1 text-sm leading-relaxed",
                read
                  ? "text-gray-500"
                  : "text-gray-700",
              ].join(" ")}
            >
              {message}
            </p>
          )}

          {formattedDate && (
            <time
              dateTime={notification.createdAt}
              className="mt-1 block text-xs text-gray-400"
            >
              {formattedDate}
            </time>
          )}
        </div>

        {/* ================================================= */}
        {/* ACTIONS                                           */}
        {/* ================================================= */}

        <div className="flex shrink-0 items-center gap-2">
          {!read && (
            <button
              type="button"
              onClick={handleMarkRead}
              disabled={
                actionLoading ||
                !id
              }
              className="rounded-md border px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              title="Mark as read"
              aria-label={`Mark ${title} as read`}
            >
              {actionLoading
                ? "Updating…"
                : "Mark Read"}
            </button>
          )}

          {redirectTo ? (
            <Link
              to={redirectTo}
              onClick={handleLinkView}
              aria-label={`View ${title}`}
              className={[
                "rounded-md border px-3 py-1.5 text-xs font-medium",
                "border-indigo-200 text-indigo-700 hover:bg-indigo-50",
                "disabled:cursor-not-allowed",
              ].join(" ")}
            >
              View
            </Link>
          ) : (
            <button
              type="button"
              onClick={handleView}
              disabled={
                actionLoading ||
                !id
              }
              aria-label={`View ${title}`}
              className="rounded-md border border-indigo-200 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              View
            </button>
          )}
        </div>
      </div>
    </li>
  );
}